import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";
import ScreenCard from "../ScreenCard";
import { BodyMediumText, BodySmallText } from "../../components/FormattedText";
import FullWidthLine from "../../components/FullWidthLine";
import { useTheme } from "../../hooks/use-theme";
import { Controller, useForm } from "react-hook-form";
import MarkdownView from "../../components/MarkdownView";
import HStack from "../../ui_atoms/HStack";
import DropdownField from "../../components/DropdownField";
import { AmendmentState, EarningType, Mission, SalaryType } from "../../__generated__/graphql";
import MoneyField from "../../components/MoneyField";
import { OptionType } from "../../components/OptionsSelector";
import { StyleSheet } from "react-native";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_MISSION } from "../../graphql/core";
import {
    formatCurrency,
    formatDateForGraphql,
    getIsPayVacationIndemnityWithSalary,
    getIsPay13thSalaryWithSalary,
    getSalaryTypeForRegime,
} from "../../utils";
import { Salaries, useComputeSalary } from "../../components/editors/salary/hooks";
import { useDebouncedCallback } from "use-debounce";
import {
    SHORT_DATE,
    SHORT_DATE_IN_LIST_FORMAT_OPTIONS,
    toISODateString,
    useDateFormatter,
    useFormatWithPeriod,
} from "../../hooks/date";
import { RootStackParamList } from "../../navigations/RootNavigation";
import Callout from "../../ui_atoms/Callout";
import {
    GET_MISSION_AMENDMENTS,
    GET_VALID_AMENDMENT_APPLY_DATES,
    SUBMIT_AMENDMENT,
} from "../../graphql/amendment";
import SelectField from "../../components/SelectField";
import { SelectItemType } from "../../components/select/SelectChooser";
import { useOpenMarkdownLink } from "../../hooks/use-open-markdown-link";
import { MarkOptional } from "ts-essentials";
import DateField from "../../components/DateField";
import { addDays } from "date-fns";
import ActivableListItem from "../../ui_atoms/ActivableListItem";
import { useCreateUpdateDeleteDraftAmendment } from "../../hooks/amendments";
import { useSetAlert } from "../../hooks/use-set-alert";
import Text from "../../components/Text";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import ScreenHeader from "./ScreenHeader";

type EditSalaryFormData = {
    grossSalary: number;
    applyDate: string;
};
type ExtendContractFormData = {
    endDate: string;
};

type AmendmentFullType = MarkOptional<
    EditSalaryFormData & ExtendContractFormData,
    "grossSalary" | "applyDate" | "endDate"
>;

type MissionEditScreenProps = StackScreenProps<
    RootStackParamList & UserMissionsTabParamList,
    "MissionEdit"
>;

const MissionEditScreen: React.FC<MissionEditScreenProps> = ({ navigation, route }) => {
    const i18n = useI18n();
    const { fonts, gridSize, colors } = useTheme();
    const styles = useStyles();
    const { missionId } = route.params;

    const { data: { getMissionAmendments = [] } = {} } = useQuery(GET_MISSION_AMENDMENTS, {
        fetchPolicy: "cache-and-network",
        variables: {
            missionId: missionId,
            includeRejected: true,
        },
    });

    const missionDraftAmendment = useMemo(
        () => getMissionAmendments.filter((a) => a.state === AmendmentState.Draft)?.[0],
        [getMissionAmendments],
    );

    const [submitAmendment, { loading: submitAmendmentLoading, error: submitAmendmentError }] =
        useMutation(SUBMIT_AMENDMENT);

    const defaultValuesFromDraftAmendment = useMemo(() => {
        const applyDate = missionDraftAmendment?.applyDate;
        const changes = (missionDraftAmendment?.changes || []).reduce(
            (result, value) => ({
                ...result,
                ...(value.field === "grossSalary" && value.newValue
                    ? {
                          [value.field]: parseInt(value.newValue) / 100,
                      }
                    : { [value.field]: value.newValue }),
            }),
            {} as AmendmentFullType,
        );
        return { ...changes, applyDate };
    }, [missionDraftAmendment]);

    const [isEditSalaryEnabled, setIsEditSalaryEnabled] = useState(
        !!defaultValuesFromDraftAmendment.grossSalary,
    );
    const [isExtendContractEnabled, setIsExtendContractEnabled] = useState(
        !!defaultValuesFromDraftAmendment.endDate,
    );

    const { data: getMissionData, error: getMissionError } = useQuery(GET_MISSION, {
        variables: { id: missionId || "" },
        fetchPolicy: "cache-and-network",
    });

    const mission = getMissionData?.getMission;
    const currentContract = getMissionData?.getMission?.currentContract;

    const {
        control: editSalaryControl,
        watch: editSalaryWatch,
        formState: { isValid: isEditSalaryFormValid, errors: editSalaryErrors },
        trigger: editSalaryTrigger,
    } = useForm<EditSalaryFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: defaultValuesFromDraftAmendment,
    });

    const {
        control: extendContractControl,
        watch: extendContractWatch,
        formState: { isValid: isExtendContractFormValid },
    } = useForm<ExtendContractFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: defaultValuesFromDraftAmendment,
    });

    const grossSalary = editSalaryWatch("grossSalary");
    const applyDate = editSalaryWatch("applyDate");
    const endDate = extendContractWatch("endDate");

    const {
        data: getValidAmendmentApplyDatesData,
        error: getValidAmendmentApplyDatesError,
        loading: getValidAmendmentApplyDatesLoading,
    } = useQuery(GET_VALID_AMENDMENT_APPLY_DATES, {
        variables: {
            missionId: missionId || "",
            data: {
                changedFields: [
                    ...(isEditSalaryEnabled ? ["grossSalary"] : []),
                    ...(isExtendContractEnabled && endDate ? ["endDate"] : []),
                ],
                monthsAhead: 6,
            },
        },
    });

    // This query is used to determine if the salary edit section should be shown, so it needs to be fetched regardless of whether the salary edit toggle is enabled.
    const {
        data: getValidAmendmentApplyDatesDataForEditSalary,
        error: getValidAmendmentApplyDatesErrorForEditSalary,
        loading: getValidAmendmentApplyDatesLoadingForEditSalary,
    } = useQuery(GET_VALID_AMENDMENT_APPLY_DATES, {
        variables: {
            missionId: missionId || "",
            data: {
                changedFields: ["grossSalary"],
                monthsAhead: 6,
            },
        },
    });

    // On a 13x contract the monthly amount paid is the base salary, so it replaces the gross. The
    // regime cannot be amended, so it only changes once the contract has loaded.
    const is13x = !getIsPay13thSalaryWithSalary(currentContract);
    const [localSalaryType, setLocalSalaryType] = useState(SalaryType.Gross);
    useEffect(() => {
        setLocalSalaryType((current) => getSalaryTypeForRegime(current, is13x));
    }, [is13x]);

    const salaryTypeKey = useMemo(() => {
        switch (localSalaryType) {
            case SalaryType.Gross:
                return "grossSalary";
            case SalaryType.Base:
                return "baseSalary";
            case SalaryType.Invoice:
                return "invoiceSalary";
            case SalaryType.Net:
                return "netSalary";
        }
    }, [localSalaryType]);

    const salaryTypeData = useMemo(
        () =>
            [
                is13x
                    ? {
                          label: i18n.t("screens.user.create_mission_salary.salaryType.base_13x"),
                          value: SalaryType.Base,
                      }
                    : {
                          label: i18n.t("screens.user.create_mission_salary.salaryType.gross"),
                          value: SalaryType.Gross,
                      },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.net"),
                    value: SalaryType.Net,
                },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.invoice"),
                    value: SalaryType.Invoice,
                },
            ] as [OptionType<SalaryType>, OptionType<SalaryType>, OptionType<SalaryType>],
        [i18n, is13x],
    );

    const contractEarningType = useMemo(
        () => getMissionData?.getMission?.currentContract?.earningType || EarningType.Monthly,
        [getMissionData?.getMission?.currentContract?.earningType],
    );

    const salaryMaxValue = useMemo(() => {
        switch (contractEarningType) {
            case EarningType.Monthly:
                return 50_000;
            case EarningType.Daily:
                return 10_000;
            case EarningType.Hourly:
                return 5_000;
            case EarningType.Package:
                return 1_000_000;
            default:
                return 0;
        }
    }, [contractEarningType]);

    const [isAwaitingSalaryDetails, setIsAwaitingSalaryDetails] = useState(false);
    const [minSalaries, setMinSalaries] = useState<Salaries | null>(null);
    // Keep the last successful min salaries so validation doesn't clear on reload/error
    const lastValidMinSalaries = useRef<Salaries | null>(null);

    const setSalaries = useCallback((salaries: Salaries | null) => {
        setIsAwaitingSalaryDetails(false);
        if (salaries !== null) {
            lastValidMinSalaries.current = salaries;
        }
        setMinSalaries(salaries);
    }, []);

    const getMinSalaryErrorMessage = useCallback(
        (minSalary: number) => {
            const percentage = currentContract?.workingPercentage || 100;

            return i18n.t(
                `screens.user.create_mission_salary_${contractEarningType}.min_salary_info_plus`,
                {
                    percentage: percentage,
                    percentageAmount: formatCurrency({
                        amountInCents: (percentage / 100) * minSalary,
                        displayDigitsWhenNoCents: false,
                    }),
                    amount: formatCurrency({
                        amountInCents: minSalary,
                        displayDigitsWhenNoCents: false,
                    }),
                    hours: currentContract?.workingTimeClassicSpec?.unitCount ?? 0,
                    salaryType: i18n
                        .t(`screens.user.create_mission_salary.salaryType.${localSalaryType}`)
                        .toLowerCase(),
                },
            );
        },
        [
            contractEarningType,
            currentContract?.workingPercentage,
            currentContract?.workingTimeClassicSpec?.unitCount,
            i18n,
            localSalaryType,
        ],
    );

    const currentContractSalaryInCents = useMemo(() => {
        switch (localSalaryType) {
            case SalaryType.Gross:
                return currentContract?.grossSalary ?? null;
            case SalaryType.Base:
                return currentContract?.baseSalary ?? null;
            case SalaryType.Net:
                return currentContract?.netSalary ?? null;
            case SalaryType.Invoice:
                return currentContract?.invoiceSalary ?? null;
            default:
                return null;
        }
    }, [currentContract, localSalaryType]);

    const validateSalaryAmount = useCallback(
        (value?: number | null) => {
            if (!value) return true; // required rule handles this

            const valueInCents = value * 100;

            if (
                currentContractSalaryInCents !== null &&
                valueInCents === currentContractSalaryInCents
            ) {
                return i18n.t("mission.edit.salary.same_salary_error");
            }

            // Use the last valid min salaries to avoid clearing the error when
            // a reload/error sets minSalaries back to null
            const effectiveMinSalaries = minSalaries ?? lastValidMinSalaries.current;
            if (!effectiveMinSalaries) return true; // never loaded yet

            const { minBaseSalary, minGrossSalary, minNetSalary, minInvoiceSalary } =
                effectiveMinSalaries;

            // For monthly contracts the backend min salary is a full-time (100%) value that
            // must be prorated to the contract's working percentage, matching the minimum
            // shown in the error message. Other earning types are compared as-is.
            const percentage = currentContract?.workingPercentage || 100;
            const toEffectiveMin = (minSalary: number) =>
                contractEarningType === EarningType.Monthly
                    ? (minSalary * percentage) / 100
                    : minSalary;

            switch (localSalaryType) {
                case SalaryType.Base:
                    if (valueInCents < toEffectiveMin(minBaseSalary)) {
                        return getMinSalaryErrorMessage(minBaseSalary);
                    }
                    break;
                case SalaryType.Gross:
                    if (valueInCents < toEffectiveMin(minGrossSalary)) {
                        return getMinSalaryErrorMessage(minGrossSalary);
                    }
                    break;
                case SalaryType.Net:
                    if (valueInCents < toEffectiveMin(minNetSalary)) {
                        return getMinSalaryErrorMessage(minNetSalary);
                    }
                    break;
                case SalaryType.Invoice:
                    if (valueInCents < toEffectiveMin(minInvoiceSalary)) {
                        return getMinSalaryErrorMessage(minInvoiceSalary);
                    }
                    break;
            }
            return true;
        },
        [
            currentContractSalaryInCents,
            minSalaries,
            localSalaryType,
            i18n,
            getMinSalaryErrorMessage,
            contractEarningType,
            currentContract?.workingPercentage,
        ],
    );

    // Revalidate newSalaryAmount when dependencies change
    useEffect(() => {
        if (grossSalary !== undefined) {
            void editSalaryTrigger("grossSalary");
        }
    }, [
        localSalaryType,
        minSalaries,
        currentContractSalaryInCents,
        grossSalary,
        salaryTypeKey,
        editSalaryTrigger,
    ]);

    const ijmGroup = mission?.currentContract?.ijmInfo?.ijmGroup;
    const ijmGroupId = ijmGroup === null ? null : ijmGroup?.id;

    const {
        computeSalary,
        salaryDetails,
        loading: computeSalaryLoading,
        error: computeSalaryError,
    } = useComputeSalary(
        mission as Mission,
        ijmGroupId,
        mission?.currentContract?.lppPlan,
        setSalaries,
    );

    const formatWithPeriod = useFormatWithPeriod();

    const editSalaryDescriptionKeys = useMemo(() => {
        const oldGrossSalary = getMissionData?.getMission?.currentContract?.grossSalary || 0;
        const oldInvoiceSalary = getMissionData?.getMission?.currentContract?.invoiceSalary || 0;

        const newGrossSalary = salaryDetails?.getSalaryDetails.grossSalary || 0;
        const newInvoiceSalary = salaryDetails?.getSalaryDetails.invoiceSalary || 0;

        return {
            oldGrossSalary: formatWithPeriod(oldGrossSalary, contractEarningType),
            oldInvoiceSalary: formatWithPeriod(oldInvoiceSalary, contractEarningType),
            ...(newGrossSalary && newInvoiceSalary
                ? {
                      newGrossSalaryRaw: newGrossSalary,
                      newGrossSalary: formatWithPeriod(newGrossSalary, contractEarningType),
                      newInvoiceSalary: formatWithPeriod(newInvoiceSalary, contractEarningType),
                  }
                : {}),
        };
    }, [
        contractEarningType,
        formatWithPeriod,
        getMissionData?.getMission?.currentContract?.grossSalary,
        getMissionData?.getMission?.currentContract?.invoiceSalary,
        salaryDetails?.getSalaryDetails.grossSalary,
        salaryDetails?.getSalaryDetails.invoiceSalary,
    ]);

    const currentContractEndDate = useMemo(() => {
        const endDate = currentContract?.effectiveEndDate || currentContract?.endDate;
        return endDate ? new Date(endDate) : undefined;
    }, [currentContract?.effectiveEndDate, currentContract?.endDate]);

    const defaultApplyDate = useMemo(() => {
        const { payrollDates } = getValidAmendmentApplyDatesData?.getValidAmendmentApplyDates || {};

        // If salary is touched, use payroll dates in priority
        if (isEditSalaryEnabled || !currentContractEndDate) {
            return new Date(
                // Is a applyDate was manually specified by user
                applyDate ||
                    // Takes the first one available in payrollDates
                    payrollDates?.find((p) => p.isValid)?.date ||
                    "",
            );
        }
        return addDays(currentContractEndDate, 1);
    }, [
        applyDate,
        currentContractEndDate,
        getValidAmendmentApplyDatesData?.getValidAmendmentApplyDates,
        isEditSalaryEnabled,
    ]);

    const refreshSalaryComputation = useCallback(() => {
        if (defaultApplyDate) {
            void computeSalary(grossSalary || 0, localSalaryType, new Date(defaultApplyDate));
        }
    }, [computeSalary, defaultApplyDate, grossSalary, localSalaryType]);

    const debouncedRefreshSalaryComputation = useDebouncedCallback(refreshSalaryComputation, 500);

    useEffect(() => {
        if (isEditSalaryEnabled) {
            setIsAwaitingSalaryDetails(true);
            debouncedRefreshSalaryComputation();
        }
    }, [
        grossSalary,
        localSalaryType,
        endDate,
        debouncedRefreshSalaryComputation,
        isEditSalaryEnabled,
    ]);

    const formattedAmendmentValues = useMemo(() => {
        return {
            applyDate: formatDateForGraphql(defaultApplyDate),
            grossSalary: isEditSalaryEnabled
                ? editSalaryDescriptionKeys.newGrossSalaryRaw
                : undefined,
            endDate:
                endDate && isExtendContractEnabled
                    ? formatDateForGraphql(new Date(endDate))
                    : undefined,
        };
    }, [
        defaultApplyDate,
        editSalaryDescriptionKeys.newGrossSalaryRaw,
        endDate,
        isEditSalaryEnabled,
        isExtendContractEnabled,
    ]);

    const { isLoading: isAmendmentLoading } = useCreateUpdateDeleteDraftAmendment({
        missionId,
        missionDraftAmendment: missionDraftAmendment,
        values: formattedAmendmentValues,
    });

    const editSalaryMarkdown = useMemo(() => {
        const [grossText, invoiceText] = i18n
            .t("mission.edit.salary.description", editSalaryDescriptionKeys)
            .split("\n");

        if (
            editSalaryDescriptionKeys.newGrossSalary &&
            editSalaryDescriptionKeys.newInvoiceSalary
        ) {
            return `${grossText} [→ ${editSalaryDescriptionKeys.newGrossSalary}]()\n${invoiceText} [→ ${editSalaryDescriptionKeys.newInvoiceSalary}]()`;
        } else {
            return `${grossText}\n${invoiceText}`;
        }
    }, [editSalaryDescriptionKeys, i18n]);

    const isLoading = useMemo(
        () =>
            computeSalaryLoading ||
            isAwaitingSalaryDetails ||
            isAmendmentLoading ||
            submitAmendmentLoading,
        [computeSalaryLoading, isAwaitingSalaryDetails, isAmendmentLoading, submitAmendmentLoading],
    );

    const isValid = useMemo(() => {
        return (
            (isEditSalaryEnabled || isExtendContractEnabled) &&
            (!isEditSalaryEnabled || isEditSalaryFormValid) &&
            (!isExtendContractEnabled || isExtendContractFormValid)
        );
    }, [
        isEditSalaryEnabled,
        isEditSalaryFormValid,
        isExtendContractEnabled,
        isExtendContractFormValid,
    ]);

    const dateFormatter = useDateFormatter();
    const { setAlert } = useSetAlert();

    const formattedAmendmentStartDatesData = useMemo<SelectItemType<string>[]>(
        () =>
            getValidAmendmentApplyDatesData?.getValidAmendmentApplyDates.payrollDates?.map(
                (dateData) => {
                    const { date, isValid } = dateData;
                    return {
                        label: dateFormatter(date, SHORT_DATE),
                        value: date,
                        ...(!isValid && currentContractEndDate
                            ? {
                                  globalTextColor: colors.greyAccessible,
                                  onPress: () => {
                                      setAlert({
                                          title: i18n.t("mission.edit.salary.invalid_date.title"),
                                          message: i18n.t(
                                              "mission.edit.salary.invalid_date.description",
                                              {
                                                  endDate: dateFormatter(
                                                      currentContractEndDate,
                                                      SHORT_DATE,
                                                  ),
                                              },
                                          ),
                                      });
                                  },
                              }
                            : {}),
                    };
                },
            ) || [],
        [
            colors.greyAccessible,
            currentContractEndDate,
            dateFormatter,
            getValidAmendmentApplyDatesData?.getValidAmendmentApplyDates,
            i18n,
            setAlert,
        ],
    );

    const missionError =
        getMissionError ||
        getValidAmendmentApplyDatesError ||
        getValidAmendmentApplyDatesErrorForEditSalary ||
        submitAmendmentError;

    useApolloErrorSnackbar(missionError);

    const salaryError = computeSalaryError || editSalaryErrors.grossSalary?.message;

    const onLinkPress = useOpenMarkdownLink();

    const isCdd = !!currentContract?.endDate;

    const hasValidPayrollDatesForEditSalary = useMemo(() => {
        const { payrollDates } =
            getValidAmendmentApplyDatesDataForEditSalary?.getValidAmendmentApplyDates || {};
        if (!payrollDates) return false;
        return payrollDates.some((p) => p.isValid);
    }, [getValidAmendmentApplyDatesDataForEditSalary?.getValidAmendmentApplyDates]);

    const onSign = useCallback(async () => {
        if (missionDraftAmendment?.id) {
            const result = await submitAmendment({
                variables: { amendmentId: missionDraftAmendment.id },
            });
            if (result.data?.submitAmendment) {
                navigation.navigate("CreateContractPreSign", {
                    contractId: missionDraftAmendment.id,
                    contractType: "amendment",
                });
            }
        }
    }, [missionDraftAmendment?.id, navigation, submitAmendment]);

    return (
        <>
            <ScreenHeader
                useSmallLabel
                onLeftAction={navigation.goBack}
                label={i18n.t("mission.edit.title")}
            />
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("mission.edit.sign"),
                        mode: "secondary",
                        isValid: isValid,
                        validating: isLoading,
                        onPress: onSign,
                    },
                ]}
            >
                <VStack h={1} v={1} gap={1}>
                    <BodyMediumText>{i18n.t("mission.edit.description")}</BodyMediumText>
                    {hasValidPayrollDatesForEditSalary && (
                        <FullWidthLine style={{ marginHorizontal: -gridSize }} />
                    )}
                    {hasValidPayrollDatesForEditSalary && (
                        <ActivableListItem
                            title={i18n.t("mission.edit.salary.title")}
                            enabled={isEditSalaryEnabled}
                            onSwitch={setIsEditSalaryEnabled}
                        >
                            <VStack gap="2/3">
                                <MarkdownView
                                    markdownStyle={{
                                        paragraph: {
                                            ...fonts.bodySmall,
                                            marginTop: 0,
                                        },
                                    }}
                                    onLinkPress={() => {
                                        navigation.navigate("SalaryCountingDetails", {
                                            earningType: contractEarningType,
                                            payVacationIndemnityWithSalary:
                                                getIsPayVacationIndemnityWithSalary(
                                                    currentContract,
                                                    mission?.legalEmployer?.useLseCct,
                                                ),
                                            useLseCct: mission?.legalEmployer?.useLseCct ?? true,
                                            pay13thSalaryWithSalary:
                                                getIsPay13thSalaryWithSalary(currentContract),
                                            salaryDetails:
                                                salaryDetails?.getSalaryDetails as RootStackParamList["SalaryCountingDetails"]["salaryDetails"],
                                        });
                                        return false;
                                    }}
                                    markdown={editSalaryMarkdown}
                                />
                                <HStack gap="2/3" wrapChildren={false}>
                                    <Controller
                                        name="grossSalary"
                                        control={editSalaryControl}
                                        rules={{
                                            required: true,
                                            shouldUnregister: true,
                                            validate: validateSalaryAmount,
                                        }}
                                        render={({
                                            field: { value, onBlur, onChange },
                                            fieldState: { error, isTouched },
                                        }) => (
                                            <MoneyField
                                                selectTextOnFocus
                                                allowNegativeValue={false}
                                                style={styles.salaryStyle}
                                                maxValue={salaryMaxValue}
                                                error={isTouched ? !!error : undefined}
                                                showErrorText={false}
                                                leftColor={colors.greyAccessible}
                                                value={value || undefined}
                                                onBlur={onBlur}
                                                onChange={onChange}
                                            />
                                        )}
                                    />
                                    <DropdownField<SalaryType>
                                        numberOfLines={1}
                                        style={{
                                            flexShrink: 1,
                                            flexGrow: 1,
                                            flexBasis: "75%",
                                        }}
                                        items={salaryTypeData}
                                        onSelect={setLocalSalaryType}
                                        value={localSalaryType}
                                    />
                                </HStack>
                                {salaryError && <Callout markdown={salaryError} mode="error" />}
                                <Controller
                                    name="applyDate"
                                    control={editSalaryControl}
                                    rules={{ required: true, validate: (e) => !!e }}
                                    render={({
                                        field: { onChange, value },
                                        fieldState: { error, isTouched },
                                    }) => (
                                        <SelectField
                                            right="calendar"
                                            topComponent={
                                                <Text style={fonts.bodyMedium}>
                                                    {i18n.t(
                                                        "mission.edit.salary.select_apply_date",
                                                    )}
                                                </Text>
                                            }
                                            disabled={
                                                getValidAmendmentApplyDatesLoading ||
                                                getValidAmendmentApplyDatesLoadingForEditSalary
                                            }
                                            label={i18n.t("mission.edit.salary.application_date")}
                                            data={formattedAmendmentStartDatesData}
                                            onSelect={(v) => onChange(v.value)}
                                            value={value}
                                            error={isTouched ? error?.message : undefined}
                                            showErrorText={false}
                                        />
                                    )}
                                />
                            </VStack>
                        </ActivableListItem>
                    )}
                    {isCdd && <FullWidthLine style={{ marginHorizontal: -gridSize }} />}
                    {isCdd && (
                        <ActivableListItem
                            title={i18n.t("mission.edit.duration.title")}
                            onSwitch={setIsExtendContractEnabled}
                            enabled={isExtendContractEnabled}
                        >
                            <VStack gap="2/3">
                                <>
                                    <BodySmallText>
                                        {i18n.t("mission.edit.duration.initialEndDateDescription", {
                                            initialEndDate: dateFormatter(
                                                currentContractEndDate || new Date(),
                                                SHORT_DATE_IN_LIST_FORMAT_OPTIONS,
                                            ),
                                        })}
                                    </BodySmallText>
                                    <MarkdownView
                                        onLinkPress={onLinkPress}
                                        markdownStyle={{
                                            paragraph: {
                                                ...fonts.bodySmall,
                                                color: colors.greyAccessible,
                                                marginTop: 0,
                                            },
                                        }}
                                        markdown={i18n.t("mission.edit.duration.description")}
                                    />
                                </>
                                <Controller
                                    name="endDate"
                                    control={extendContractControl}
                                    rules={{
                                        shouldUnregister: true,
                                        required: true,
                                    }}
                                    render={({
                                        field: newContractEndDateField,
                                        fieldState: { error, isTouched },
                                    }) => (
                                        <DateField
                                            label={i18n.t("mission.edit.duration.newEndDate")}
                                            onChange={(v) => {
                                                newContractEndDateField.onChange(
                                                    toISODateString(v),
                                                );
                                            }}
                                            value={
                                                newContractEndDateField.value
                                                    ? new Date(newContractEndDateField.value)
                                                    : undefined
                                            }
                                            error={isTouched ? error?.message : undefined}
                                            showErrorText={false}
                                            minimumDate={addDays(
                                                currentContractEndDate || new Date(),
                                                1,
                                            )}
                                        />
                                    )}
                                />
                            </VStack>
                        </ActivableListItem>
                    )}
                </VStack>
            </ScreenCard>
        </>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                salaryStyle: {
                    marginTop: 0,
                    flexShrink: 1,
                    flexGrow: 1,
                    flexBasis: "auto",
                },
            }),
        [],
    );
};

export default MissionEditScreen;
